import React, { useEffect, useRef } from "react";
import { useContext } from "react";
import { observer } from "mobx-react";
import { Icon } from "@blueprintjs/core";

import { isServer, StoresContext } from "../../pages/_app";
import { InfoTooltip } from "../info-tooltip";

// This uses the concept of an open-source library: https://github.com/MattX/highlight-within-textarea
// We don't use it directly because it creates/owns DOM objects and we want to highlight 2 ranges at once when
// hovering.

function scrubText(sequenceText: string): string {
  return sequenceText
    .replace(/[>@].*$/gm, "")
    .replace(/\s/g, "")
    .replace(/[^actgnACTGN]/g, "N");
}

export const SequenceSearchTextarea = observer(() => {
  const rootStore = useContext(StoresContext);
  const store = rootStore.genomeStore;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.addEventListener("resize", updateTextareaHeight);
    return () => window.removeEventListener("resize", updateTextareaHeight);
  }, []);

  useEffect(() => {
    const inputVal = scrubText(store.sequenceSearchValue);
    if (textareaRef.current) {
      textareaRef.current.value = inputVal;
      updateTextareaHeight();
    }
  }, [store.sequenceSearchValue]);

  const updateTextareaHeight = () => {
    if (textareaRef.current) {
      // https://stackoverflow.com/a/30051062/3776794
      textareaRef.current.style.height = "1px";
      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
    }
  };

  const getHighlightedSequence = () => {
    // Default to first color when there isn't any specific genome highlighted
    const color = store.hoveredGenome ? store.hoveredGenome.color : 0;
    const res = [];

    // Not very optimal as we append 31 tags if a full sequence match instead of only 1 but simpler
    // It isn't visibly slow but if there is an issue that is an easy pick.
    for (let i = 0; i < store.sequenceSearchValue.length; i += 1) {
      if (store.hoveredGenomeHighlightedBases()[i]) {
        res.push(`<mark class="result-mark-${color}">${store.sequenceSearchValue[i]}</mark>`);
      } else if (store.allHighlightedBases()[i]) {
        res.push(`<mark class="result-mark-0">${store.sequenceSearchValue[i]}</mark>`);
      } else {
        res.push(store.sequenceSearchValue[i]);
      }
    }
    return res.join("");
  };

  const onChange = () => {
    textareaRef.current!.value = scrubText(textareaRef.current!.value);
    store.addSearchFilter("sequence", textareaRef.current!.value);
  };

  // Browser detection is meh but in this case the browser differ too much that I couldn't figure out a single
  // way to make it work.
  // The issue is how browsers interpret margin/padding when scrolling in textareas: FF keep the padding at all times while
  // Chrome/Safari only keeps it at the very top/bottom. This end up messing up the highlighting because the padding
  // is inconsistent.
  // There are 2 solutions:
  //   - FF: margin: 22px (20 of padding + 1*2 for borders) on the backdrop and nothing on the highlights.
  //   - Chrome/Safari: nothing on backdrop but 20px of padding + 1px border to match textarea on highlights
  // The default is Chrome/Safari since that is more common than FF.
  // Related bug in Chrome: https://bugs.chromium.org/p/chromium/issues/detail?id=81560 (2011)
  const isFirefox = !isServer ? typeof window.InstallTrigger !== "undefined" : false;

  if (store.sequenceSearchFilter === null) {
    return null;
  }

  return (
    <div className="margin-bottom-sm">
      <div className="sequence-search-header">
        <h3>Nucleotide search for {store.sequenceSearchValue.length.toLocaleString()}bp query</h3>
        <InfoTooltip>
          Bases that match the genomic sequence of a genome published on the portal are highlighted
          in gray. Upon rollover, bases that match the genomic sequence of a genome in the search
          results are highlighted in an additional color.
        </InfoTooltip>
      </div>
      <div className="sequence-search-textarea">
        <div className="backdrop" ref={backdropRef} style={isFirefox ? { margin: 30 } : {}}>
          <div
            className="highlights"
            dangerouslySetInnerHTML={{ __html: getHighlightedSequence() }}
            style={isFirefox ? { padding: 0, border: "unset" } : {}}
          />
        </div>
        <textarea
          ref={textareaRef}
          onChange={onChange}
          spellCheck={false}
          placeholder="Enter or paste a nucleotide sequence here to find all of the genomes that match your query"
          autoComplete="false"
          autoCorrect="false"
        />
        <Icon
          icon="cross"
          className="close-icon"
          onClick={() => store.removeSearchFilter("sequence", null)}
        />
      </div>
    </div>
  );
});
